import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HEROES } from '../data/heroes';
import { ACTIVITY_CATALOG } from '../data/activityCatalog';

/**
 * HeroPathSection — one hero card on the Activities "Choose Your Hero" tab.
 * Collapsed: shows 6 activity quick-pick chips + "See all" expander.
 * Expanded: shows ALL activities for that hero (inline grid).
 */
const HeroPathSection = ({ heroKey, expanded, onToggle, onStartHeroLed }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const hero = HEROES[heroKey];
  const tr = t(`heroes.${heroKey}`, { returnObjects: true });
  const acts = ACTIVITY_CATALOG[heroKey];
  const visible = expanded ? acts : acts.slice(0, 6);

  return (
    <Card
      data-testid={`hero-path-${heroKey}`}
      className="bs-glass border-0 overflow-hidden"
    >
      <div className={`h-1.5 bg-gradient-to-r ${hero.gradient}`} />
      <div className="grid md:grid-cols-[220px_1fr] gap-0">
        <div className={`${hero.imageBg} flex items-end justify-center p-3`}>
          <img src={hero.image} alt={tr.name} className="max-h-56 w-auto object-contain drop-shadow-2xl" />
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
            <div>
              <h3 className="bs-title text-2xl font-extrabold">{tr.name}</h3>
              <p className="text-xs uppercase tracking-widest bs-amber font-bold">{tr.subname}</p>
            </div>
            <Badge className="bg-white/10 text-amber-300 border-amber-300/30">
              {t('heroLed.activitiesCount', { n: acts.length })}
            </Badge>
          </div>
          <p className="italic font-semibold text-white mb-3">"{tr.tagline}"</p>
          <p className="text-xs bs-muted tracking-wide mb-4">{tr.mantra}</p>
          <div
            className={`grid grid-cols-2 sm:grid-cols-3 gap-2 transition-all`}
            data-testid={`hero-activities-grid-${heroKey}`}
          >
            {visible.map((a) => (
              <button
                key={a.id}
                onClick={() => onStartHeroLed(heroKey, a)}
                data-testid={`hero-activity-${heroKey}-${a.id}`}
                className="text-left bs-glass-soft px-3 py-2 hover:bg-white/15 transition-colors"
              >
                <p className="text-xs bs-amber font-bold">{a.duration} {t('heroLed.minute')}</p>
                <p className="text-sm text-white font-semibold leading-tight truncate">{a.title[lang]}</p>
              </button>
            ))}
          </div>
          <button
            onClick={onToggle}
            className="bs-amber mt-4 text-sm font-semibold inline-flex items-center gap-1"
            data-testid={`see-all-${heroKey}`}
            aria-expanded={expanded}
          >
            {expanded
              ? (lang === 'es' ? 'Mostrar menos' : 'Show less')
              : (lang === 'es' ? `Ver todas las ${acts.length} actividades` : `See all ${acts.length} activities`)}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </Card>
  );
};

export default HeroPathSection;
